import {
  Component,
  DOCUMENT,
  ElementRef,
  HostListener,
  Input,
  OnDestroy,
  Renderer2,
  Signal,
  ViewChild,
  computed,
  effect,
  inject,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { NavigationEnd, Router } from '@angular/router';
import { filter } from 'rxjs/operators';

import { Menu } from '../../models/menu.model';
import { MENU_HINTS } from '../../data/menu.data';
import { RUTA_ACCESO } from '../../guards/auth.guard';
import { AuthService } from '../../services/auth.service';
import { UiService } from '../../services/ui.service';

/** Todo lo que puede recibir foco dentro del cajón. */
const FOCUSABLE: string =
  'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), ' +
  'textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

/**
 * Panel lateral de cristal: las cinco secciones con la pregunta que responden.
 *
 * Tiene tres modos (docs/RESPONSIVE.md §3): `full` (270px), `rail` (87px de
 * iconos) y `overlay`, que por debajo de 1024px lo saca del flujo y lo convierte
 * en un cajón modal. En `overlay` este componente aporta lo que un cajón exige:
 * fondo oscurecido, `Escape`, foco atrapado, cierre al navegar, `inert` mientras
 * está cerrado y bloqueo del scroll del fondo.
 */
@Component({
  selector: 'app-side-bar',
  templateUrl: './side-bar.component.html',
  styleUrl: './side-bar.component.scss',
  standalone: false,
})
export class SideBarComponent implements OnDestroy {

  @Input() public menuData: Array<Menu> = [];

  /** Pregunta de cada sección, indexada por ruta. */
  public readonly hints: Record<string, string> = MENU_HINTS;

  public readonly authService: AuthService = inject(AuthService);

  public readonly uiService: UiService = inject(UiService);

  public readonly isOverlay: Signal<boolean> = computed(() => this.uiService.sidebarMode() === 'overlay');

  public readonly isRail: Signal<boolean> = computed(() => this.uiService.sidebarMode() === 'rail');

  /** Cajón desplegado: sólo tiene sentido en `overlay`. */
  public readonly drawerOpen: Signal<boolean> = computed(() => this.isOverlay() && this.uiService.sidebarOpen());

  /** Cajón fuera de la vista: se marca `inert` para que el tabulador no se meta
   *  en un menú que el usuario no ve. */
  public readonly drawerClosed: Signal<boolean> = computed(() => this.isOverlay() && !this.uiService.sidebarOpen());

  @ViewChild('panel') private panelRef?: ElementRef<HTMLElement>;

  private readonly document: Document = inject(DOCUMENT);
  private readonly renderer: Renderer2 = inject(Renderer2);
  private readonly router: Router = inject(Router);

  /** Evita soltar el scroll del `body` que bloqueó otro panel (el de filtros). */
  private scrollLocked: boolean = false;

  constructor() {
    effect(() => {
      if (!this.drawerOpen()) {
        this.releaseScroll();

        return;
      }

      this.lockScroll();
      // El cajón se pinta en el siguiente ciclo: el foco va después.
      setTimeout(() => this.panelRef?.nativeElement.focus());
    });

    // Sin esto el usuario toca «Explorar» y se queda mirando el menú encima de
    // la página nueva.
    this.router.events
      .pipe(
        filter((event: unknown): event is NavigationEnd => event instanceof NavigationEnd),
        takeUntilDestroyed(),
      )
      .subscribe(() => this.uiService.closeSidebar());
  }

  public ngOnDestroy(): void {
    this.releaseScroll();
  }

  /* -------------------------------------------------------------------------- */
  /*                              ANCHOR API PÚBLICA                            */
  /* -------------------------------------------------------------------------- */

  /**
   * Cierra la sesión y sale del tablero.
   *
   * El cajón se cierra **a mano** y no por la suscripción a `NavigationEnd`:
   * esta navegación destruye el propio panel, así que `takeUntilDestroyed` da
   * de baja la suscripción antes de que el evento llegue. Sin esto,
   * `sidebarOpen` se queda en `true` dentro de `UiService` —que es de raíz y
   * sobrevive— y el tablero reaparece con el menú abierto al volver a entrar.
   */
  public salir(): void {
    this.uiService.closeSidebar();
    this.authService.logout();
    this.router.navigate([RUTA_ACCESO]);
  }

  /* -------------------------------------------------------------------------- */
  /*                               ANCHOR TECLADO                               */
  /* -------------------------------------------------------------------------- */

  @HostListener('document:keydown', ['$event'])
  public onKeydown(event: KeyboardEvent): void {
    if (!this.drawerOpen()) {
      return;
    }

    if (event.key === 'Escape') {
      // Un desplegable abierto dentro del cajón ya consumió la tecla: cerrarlo
      // no debe cerrar además el cajón entero.
      if (event.defaultPrevented) {
        return;
      }

      event.preventDefault();
      this.uiService.closeSidebar();

      return;
    }

    if (event.key === 'Tab') {
      this.trapFocus(event);
    }
  }

  /** Mantiene el tabulador dentro del cajón mientras está abierto. */
  private trapFocus(event: KeyboardEvent): void {
    const root: HTMLElement | undefined = this.panelRef?.nativeElement;

    if (!root) {
      return;
    }

    const items: Array<HTMLElement> = Array.from(root.querySelectorAll<HTMLElement>(FOCUSABLE))
      .filter((element: HTMLElement) => element.offsetParent !== null);
    const active: HTMLElement | null = this.document.activeElement as HTMLElement | null;

    if (!items.length) {
      event.preventDefault();
      root.focus();

      return;
    }

    const first: HTMLElement = items[0];
    const last: HTMLElement = items[items.length - 1];

    if (!active || !root.contains(active)) {
      event.preventDefault();
      (event.shiftKey ? last : first).focus();

      return;
    }

    if (event.shiftKey && (active === first || active === root)) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && active === last) {
      event.preventDefault();
      first.focus();
    }
  }

  /* -------------------------------------------------------------------------- */
  /*                          ANCHOR SCROLL DEL BODY                            */
  /* -------------------------------------------------------------------------- */

  /** Bloquea el scroll del fondo y compensa el ancho de la barra para que la
   *  página no salte al abrir. */
  private lockScroll(): void {
    if (this.scrollLocked) {
      return;
    }

    const body: HTMLElement = this.document.body;
    const gap: number = window.innerWidth - this.document.documentElement.clientWidth;

    this.renderer.setStyle(body, 'overflow', 'hidden');

    if (gap > 0) {
      this.renderer.setStyle(body, 'padding-right', `${gap}px`);
    }

    this.scrollLocked = true;
  }

  private releaseScroll(): void {
    if (!this.scrollLocked) {
      return;
    }

    this.renderer.removeStyle(this.document.body, 'overflow');
    this.renderer.removeStyle(this.document.body, 'padding-right');
    this.scrollLocked = false;
  }
}
